<template>
  <basic-page title="Harjoitussuunnitelmat">
    <template v-slot:app-bar-buttons>
      <v-btn icon title="Päivitä" :loading="loading" @click="load">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </template>

    <v-row class="fill-height">
      <v-col cols="12" lg="8" offset-lg="2" xl="8" offset-xl="2">
        <v-card outlined>
        <v-card-title
          >Harjoitussuunnitelmat
          <v-spacer />
          <v-btn
            color="primary"
            rounded
            small
            :disabled="loading || !isAdmin"
            @click="createNew"
          >
            <v-icon>mdi-plus</v-icon> Uusi harjoitussuunnitelma
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-alert v-if="error" type="error" dense text>
            {{ error }}
          </v-alert>

          <v-data-table
            :headers="headers"
            :items="items"
            :loading="loading"
            item-key="id"
            class="elevation-0"
            @click:row="open"
          >
            <template v-slot:item.creator="{ item }">
              {{ item.creator || "" }}
            </template>

            <template v-slot:item.updatedAt="{ item }">
              {{ formatTs(item.updatedAt) }}
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn icon small title="Avaa" @click.stop="open(item)">
                <v-icon>mdi-open-in-new</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                title="Poista"
                :disabled="!canEditItem(item)"
                @click.stop="remove(item)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>

            <template v-slot:no-data> Ei harjoitussuunnitelmia </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</basic-page>
</template>

<script>
import BasicPage from "@/components/BasicPage.vue";
import { firestore as db, auth } from "@/plugins/firebase.app";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import dayjs from "dayjs";

export default {
  name: "Harjoitussuunnitelmat",
  components: { BasicPage },
  data() {
    return {
      loading: false,
      error: null,
      headers: [
        { text: "Aihe", value: "title" },
        { text: "Luoja", value: "creator" },
        { text: "Päivä", value: "date" },
        { text: "Paikka", value: "location" },
        { text: "Päivitetty", value: "updatedAt" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      items: [],
    };
  },
  computed: {
    isAdmin() {
      try {
        return typeof this.$isAdmin === "function"
          ? this.$isAdmin()
          : Boolean(this.$isAdmin);
      } catch (e) {
        return false;
      }
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    formatTs(ts) {
      try {
        if (!ts) return "";
        const date =
          typeof ts?.toDate === "function" ? ts.toDate() : new Date(ts);
        return dayjs(date).format("D.M.YYYY HH:mm");
      } catch (e) {
        return "";
      }
    },
    canEditItem(item) {
      if (!this.isAdmin) return false;
      const uid = auth?.currentUser?.uid;
      if (!uid) return false;
      if (!item) return false;

      // Legacy docs without ownerUid/allowOthersEdit: keep old behavior
      if (!item.ownerUid) return true;
      if (item.ownerUid === uid) return true;
      return item.allowOthersEdit !== false;
    },
    createNew() {
      this.$router.push({ name: "Harjoitussuunnitelma", query: {} });
    },
    open(item) {
      if (!item?.id) return;
      this.$router.push({
        name: "Harjoitussuunnitelma",
        query: { id: item.id },
      });
    },
    async remove(item) {
      if (!item?.id) return;
      if (!this.canEditItem(item)) {
        this.$toast.error(
          this.isAdmin
            ? "Luoja on estänyt muiden muokkauksen"
            : "Vain admin voi poistaa harjoitussuunnitelmia"
        );
        return;
      }
      const ok = confirm(
        `Poistetaanko harjoitussuunnitelma "${item.title || "(ei otsikkoa)"}"?`
      );
      if (!ok) return;

      const user = auth.currentUser;
      if (!user) {
        this.$toast.error("Kirjaudu sisään");
        return;
      }

      try {
        await deleteDoc(doc(db, "harjoitussuunnitelmat", item.id));
        this.items = this.items.filter((x) => x.id !== item.id);
        this.$toast.success("Poistettu");
      } catch (e) {
        console.error(e);
        this.$toast.error("Poisto epäonnistui");
      }
    },
    async load() {
      this.error = null;
      this.loading = true;
      try {
        const user = auth.currentUser;
        if (!user) {
          this.items = [];
          this.error = "Kirjaudu sisään nähdäksesi harjoitussuunnitelmat";
          return;
        }

        const q = query(
          collection(db, "harjoitussuunnitelmat"),
          orderBy("updatedAt", "desc"),
          limit(200)
        );
        const snap = await getDocs(q);
        this.items = snap.docs.map((d) => {
          const data = d.data() || {};
          const createdBy = data?.createdBy || {};
          const creator =
            createdBy?.user ||
            createdBy?.email ||
            data?.ownerName ||
            data?.ownerEmail ||
            data?.ownerUid ||
            "";
          return { id: d.id, creator, ...data };
        });
      } catch (e) {
        console.error(e);
        this.error = "Haku epäonnistui";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
